import * as Yup from 'yup';
import { useMemo, useEffect, useState } from 'react';
import { useFormik, Form, FormikProvider } from 'formik';
import PropTypes from 'prop-types';
import { useIntl, FormattedMessage } from 'react-intl';
// material
import { Stack, TextField, Button, Grid, InputLabel, Select, FormControl, MenuItem, Chip, OutlinedInput } from '@mui/material';
import { LoadingButton } from '@mui/lab';

import { useAuth } from '../../../contexts/authContext';
import constants from '../../../constants';
import BackDropLoader from '../../../components/BackDropLoader';
import { add } from 'date-fns';
import { getNewDate } from '../../../utils';
import useDatePicker from '../../../hooks/useDatePicker';
import { getUserLocations } from '../../../features/users/services/usersService';

// ----------------------------------------------------------------------

export default function UserForm({ user, onSubmit, editOn = true, onCancel, changableLocations = false }) {
  const auth = useAuth();
  const intl = useIntl();
  const existingUser = Boolean(user);
  const DatePicker = useDatePicker();
  const [locations, setLocations] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);

  useEffect(() => {
    async function fetchLocations() {
      try {
        const data = await getUserLocations(auth.user.id);
        setLocations(data);
      } catch (error) {
        console.error('Error fetching locations', error.message);
      }
    }
    fetchLocations();
    if (user?.locations && Array.isArray(user.locations)) {
      setSelectedLocations(user.locations.map(location => location.id));
    } else {
      setSelectedLocations([]);
    }
  }, [auth.user.id, user]);

  const UserSchema = Yup.object().shape({
    name: Yup.string().min(2, 'user.form.name.requirements').max(50, 'user.form.name.requirements').required('user.form.name.required'),
    surname: Yup.string().min(2, 'user.form.lastname.requirements').max(50, 'user.form.lastname.requirements').required('user.form.lastname.required'),
    email: Yup.string().email('common.form.email.requirements').required('common.form.email.required'),
    memberSince: Yup.date().nullable(true).required('common.validation.required'),
    validTo: Yup.date().nullable(true).required('common.validation.required'),
    validFrom: Yup.date().nullable(true).required('common.validation.required'),
    locations: Yup.array().min(1, 'user.form.location.required').required('user.form.location.required'),
  });

  const dateNow = getNewDate();

  const handleLocationChange = (event) => {
    const { value } = event.target;
    setSelectedLocations(value);
    setFieldValue('locations', value);
  };

  const formik = useFormik({
    initialValues: user ?? {
      name: '',
      surname: '',
      email: '',
      memberSince: dateNow,
      validFrom: dateNow,
      validTo: add(dateNow, { months: 1 }),
      roleId: constants.roles.USER.id,
      locations: []
    },
    validationSchema: UserSchema,
    onSubmit: (values) => {
      onSubmit({ ...values, locations: selectedLocations });
    },
  });

  const { errors, touched, handleSubmit, isSubmitting, getFieldProps, dirty, setFieldValue, isValid, resetForm, setTouched } = formik;

  const handleCancel = () => {
    resetForm();
    if (onCancel) onCancel();
  };

  const roles = useMemo(() => Object.values(constants.roles).filter(item => item.id >= auth.user.roleId), [auth.user.roleId]);

  return (
    <FormikProvider value={formik}>
      <BackDropLoader open={isSubmitting} />
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              required
              fullWidth
              disabled={!editOn}
              label={intl.formatMessage({
                id: 'user.form.label.firstname',
                defaultMessage: 'First name'
              })}
              {...getFieldProps('name')}
              error={Boolean(touched.name && errors.name)}
              helperText={touched.name && errors.name && <FormattedMessage id={formik.errors.name} defaultMessage={formik.errors.name} />}
            />

            <TextField
              required
              fullWidth
              disabled={!editOn}
              label={intl.formatMessage({
                id: 'user.form.label.lastname',
                defaultMessage: 'Last name'
              })}
              {...getFieldProps('surname')}
              error={Boolean(touched.surname && errors.surname)}
              helperText={touched.surname && errors.surname && <FormattedMessage id={formik.errors.surname} defaultMessage={formik.errors.surname} />}
            />
          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              required
              fullWidth
              disabled={!editOn}
              autoComplete="username"
              type="email"
              label={intl.formatMessage({
                id: 'user.form.label.emailAddress',
                defaultMessage: 'Email address'
              })}
              {...getFieldProps('email')}
              error={Boolean(touched.email && errors.email)}
              helperText={touched.email && errors.email && <FormattedMessage id={formik.errors.email} defaultMessage={formik.errors.email} />}
            />

            <FormControl fullWidth sx={{ m: 1, minWidth: 120 }}>
              <InputLabel id='user-form-label-role'>
                <FormattedMessage id='user.form.label.role' defaultMessage='Role' />
              </InputLabel>
              <Select
                disabled={!editOn || Boolean(existingUser)}
                required
                labelId='user.form.label.role'
                {...getFieldProps('roleId')}
                label={intl.formatMessage({
                  id: 'user.form.label.role',
                  defaultMessage: 'Role'
                })}
              >
                {roles.map(item => <MenuItem key={item.id} value={item.id}>{item.name}</MenuItem>)}
              </Select>
            </FormControl>

          </Stack>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <DatePicker
              label={intl.formatMessage({
                id: 'user.form.label.memberSince',
                defaultMessage: 'Member Since'
              })}
              {...getFieldProps('memberSince')}
              onChange={(value) => {
                setTouched({ ...touched, memberSince: true });
                setFieldValue('memberSince', value, true);
              }}
              disabled={!editOn 
                || (Boolean(existingUser) && auth.user.roleId === constants.roles.USER.id) 
                || (Boolean(existingUser) && auth.user.roleId === constants.roles.SUPERUSER.id)}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    required
                    fullWidth
                    error={Boolean(touched.memberSince && errors.memberSince)}
                    helperText={touched.memberSince && errors.memberSince && <FormattedMessage id={formik.errors.memberSince} defaultMessage={formik.errors.memberSince} />}
                  />);
              }}
            />
            <DatePicker
              label={intl.formatMessage({
                id: 'user.form.label.validFrom',
                defaultMessage: 'Valid From'
              })}
              {...getFieldProps('validFrom')}
              onChange={(value) => {
                setTouched({ ...touched, validFrom: true });
                setFieldValue('validFrom', value, true);
              }}
              disabled={!editOn 
                || (Boolean(existingUser) && auth.user.roleId === constants.roles.USER.id) 
                || (Boolean(existingUser) && auth.user.roleId === constants.roles.SUPERUSER.id)}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    required
                    fullWidth
                    error={Boolean(errors.validFrom)}
                    helperText={errors.validFrom && <FormattedMessage id={formik.errors.validFrom} defaultMessage={formik.errors.validFrom} />}
                  />);
              }}
            />
            <DatePicker
              label={intl.formatMessage({
                id: 'user.form.label.validTo',
                defaultMessage: 'Valid To'
              })}
              {...getFieldProps('validTo')}
              onChange={(value) => {
                setTouched({ ...touched, validTo: true });
                setFieldValue('validTo', value, true);
              }}
              disabled={!editOn 
                || (Boolean(existingUser) && auth.user.roleId === constants.roles.USER.id) 
                || (Boolean(existingUser) && auth.user.roleId === constants.roles.SUPERUSER.id)}
              renderInput={(params) => {
                return (
                  <TextField
                    {...params}
                    required
                    fullWidth
                    error={Boolean(touched.validTo && errors.validTo)}
                    helperText={touched.validTo && errors.validTo && <FormattedMessage id={formik.errors.validTo} defaultMessage={formik.errors.validTo} />}
                  />);
              }}
            />
          </Stack>
          
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel id="locations-label">
                {intl.formatMessage({ id: 'user.form.locations', defaultMessage: 'Locations' })}
              </InputLabel>
              <Select
                labelId="locations-label"
                multiple
                value={selectedLocations}
                onChange={handleLocationChange}
                input={<OutlinedInput label="Locations" />}
                renderValue={(selected) => (
                  <Stack direction="row" spacing={1}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={locations.find((location) => location.id === value)?.name || value}
                      />
                    ))}
                  </Stack>
                )}
                disabled={!changableLocations}
              >
                {locations.map((location) => (
                  <MenuItem key={location.id} value={location.id}>
                    {location.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>

          {
            editOn &&
            <Grid container justifyContent="flex-end" sx={{ paddingTop: 0 }}>
              <Button onClick={handleCancel} disabled={isSubmitting}>
                <FormattedMessage id='common.button.cancel' defaultMessage='Cancel' />
              </Button>
              <LoadingButton type="submit" loading={isSubmitting} disabled={!dirty || !isValid} >
                <FormattedMessage id={`common.button.${existingUser ? 'update' : 'create'}`} defaultMessage='Create/Update' />
              </LoadingButton>
            </Grid>
          }
        </Stack>
      </Form>
    </FormikProvider>
  );
}

UserForm.propTypes = {
  user: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    surname: PropTypes.string.isRequired,
    email: PropTypes.string.isRequired,
    locations: PropTypes.array.isRequired,
  }),
  onSubmit: PropTypes.func.isRequired,
  editOn: PropTypes.bool,
  onCancel: PropTypes.func,
  changableLocations: PropTypes.bool
};
